"use client";

import { forwardRef, useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { RiRobot2Line } from "react-icons/ri";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { useFormField } from "../ui/form";
import { ScrollArea } from "../ui/scroll-area";
import type { Option } from "./select-option.types";

type ComboboxProps = {
  value?: Option;
  items: Option[];
  disabled?: boolean;
  placeholder?: string;
  onChange?: (value: Option | undefined) => void;
  showAll?: boolean;
};

const Combobox = forwardRef<React.ElementRef<typeof Command>, ComboboxProps>(
  (props, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const { error } = useFormField();
    const hasError = !!error?.message?.length;

    const filteredItems = props.items
      .filter((item) => item.label.toLowerCase().includes(search.toLowerCase()))
      .slice(0, props.showAll ? undefined : 4);

    return (
      <div ref={containerRef}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger disabled={props.disabled} asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(
                "w-full justify-between focus:outline-none hover:border-foreground/40 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 ring-offset-background",
                {
                  "border-destructive focus-visible:ring-destructive": hasError,
                },
              )}
            >
              <span className="flex min-w-0 items-center gap-1">
                {props.value?.value?.startsWith("agent:") && (
                  <RiRobot2Line className="shrink-0 size-3 text-muted-foreground" />
                )}
                <span className="truncate">
                  {props.value?.label?.length ? props.value.label : "Select"}
                </span>
              </span>
              <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            style={{ width: containerRef.current?.clientWidth }}
            className="p-0"
          >
            <Command ref={ref} shouldFilter={false}>
              <CommandInput
                onValueChange={setSearch}
                placeholder={props.placeholder}
              />
              <CommandList>
                <CommandEmpty>Not found</CommandEmpty>
                <CommandGroup>
                  <ScrollArea
                    className={cn(
                      "min-h-10 [&>div>div]:!flex [&>div>div]:flex-col",
                      props.showAll ? "h-36" : "h-32",
                    )}
                  >
                    {filteredItems?.map((item) => (
                      <CommandItem
                        key={item.value}
                        value={item.value}
                        onSelect={(currentValue) => {
                          const nextValue =
                            currentValue === props.value?.value
                              ? undefined
                              : item;
                          setSearch("");
                          props.onChange?.(nextValue);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 size-4 shrink-0",
                            props.value?.value === item.value
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        <span className="flex min-w-0 items-center gap-1">
                          {item.value.startsWith("agent:") && (
                            <RiRobot2Line className="shrink-0 size-3 text-muted-foreground" />
                          )}
                          <span className="truncate">{item.label}</span>
                        </span>
                      </CommandItem>
                    ))}
                  </ScrollArea>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

Combobox.displayName = "Combobox";

export default Combobox;
